import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { 
  FileText, Download, Calendar, Users, Target, 
  TrendingUp, Award, BookOpen, Sparkles, CheckCircle
} from 'lucide-react';

const ReportGeneration = () => {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState('monthly');
  const [format, setFormat] = useState('pdf');
  const [includeStory, setIncludeStory] = useState(true);
  const [selectedEngagement, setSelectedEngagement] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [generating, setGenerating] = useState(false);

  const reportTypes = [
    { 
      id: 'monthly', 
      name: 'Monthly Progress Report', 
      icon: Calendar,
      description: 'Comprehensive monthly summary of all activities and achievements'
    },
    { 
      id: 'quarterly', 
      name: 'Quarterly Impact Report', 
      icon: TrendingUp,
      description: 'Detailed quarterly analysis with KPI tracking and impact metrics'
    },
    { 
      id: 'annual', 
      name: 'Annual CSR Report', 
      icon: Award,
      description: 'Year-end comprehensive report for stakeholders and compliance'
    },
    { 
      id: 'story', 
      name: 'Impact Story Report', 
      icon: BookOpen,
      description: 'Narrative-driven report highlighting human impact and success stories'
    },
    { 
      id: 'financial', 
      name: 'Financial Utilization Report', 
      icon: Target,
      description: 'Budget allocation, disbursements, and utilization analysis'
    },
    { 
      id: 'compliance', 
      name: 'Compliance & Audit Report', 
      icon: CheckCircle,
      description: 'CSR-2, Schedule VII compliance with auditor-ready documentation'
    }
  ];

  const handleGenerateReport = async () => {
    setGenerating(true);
    
    // Simulate report generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock report generation
    const reportData = generateMockReport(reportType);
    
    alert(`Report generated successfully!\n\nType: ${reportType}\nFormat: ${format}\nStory Mode: ${includeStory ? 'Enabled' : 'Disabled'}\n\nIn production, this would download a ${format.toUpperCase()} file.`);
    
    setGenerating(false);
  };

  const generateMockReport = (type) => {
    const reports = {
      monthly: {
        title: 'Monthly Progress Report - November 2024',
        sections: [
          'Executive Summary',
          'Project Activities',
          'Milestone Achievements',
          'Financial Summary',
          'Challenges & Solutions',
          'Next Month Plan'
        ]
      },
      story: {
        title: 'Impact Story: Transforming Lives Through Skill Development',
        narrative: 'This quarter, our CSR initiatives reached 500+ beneficiaries across 3 states...'
      }
    };
    return reports[type] || reports.monthly;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{color: '#1E3A5F'}}>
              Report Generation & Storytelling
            </h1>
            <p className="text-gray-600">Create comprehensive reports and impact narratives</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Panel - Configuration */}
            <div className="md:col-span-2 space-y-6">
              {/* Report Type Selection */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
                  Select Report Type
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {reportTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setReportType(type.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-left hover:shadow-md ${
                        reportType === type.id
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-teal-200'
                      }`}
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <type.icon 
                          className="w-6 h-6 flex-shrink-0" 
                          style={{color: reportType === type.id ? '#14B8A6' : '#6B7280'}} 
                        />
                        <div className="flex-1">
                          <div 
                            className="font-semibold text-sm mb-1"
                            style={{color: reportType === type.id ? '#14B8A6' : '#1E3A5F'}}
                          >
                            {type.name}
                          </div>
                          <div className="text-xs text-gray-600">
                            {type.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Configuration Options */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
                  Report Configuration
                </h3>
                
                <div className="space-y-6">
                  {/* Date Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                      />
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>

                  {/* Format Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Export Format
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {['pdf', 'excel', 'word'].map((fmt) => (
                        <button
                          key={fmt}
                          onClick={() => setFormat(fmt)}
                          className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${
                            format === fmt
                              ? 'border-teal-500 bg-teal-50 text-teal-700'
                              : 'border-gray-200 hover:border-teal-200'
                          }`}
                        >
                          {fmt.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Storytelling Toggle */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-start gap-4">
                      <button
                        onClick={() => setIncludeStory(!includeStory)}
                        className={`w-12 h-6 rounded-full transition-all relative ${
                          includeStory ? 'bg-teal-600' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${
                          includeStory ? 'right-0.5' : 'left-0.5'
                        }`}></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="w-5 h-5" style={{color: '#14B8A6'}} />
                          <span className="font-semibold" style={{color: '#1E3A5F'}}>
                            AI-Powered Storytelling Mode
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Transform data into compelling narratives that highlight human impact and success stories
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Story Mode Options */}
                  {includeStory && (
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-3" style={{color: '#14B8A6'}}>
                        Storytelling Features
                      </h4>
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                          <span>Beneficiary success stories and testimonials</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                          <span>Visual journey from baseline to impact</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                          <span>Before-after comparisons with evidence</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                          <span>SDG alignment narrative with real examples</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                          <span>Community transformation highlights</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Preview & Actions */}
            <div className="space-y-6">
              {/* Report Preview */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 sticky top-24">
                <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
                  Report Preview
                </h3>
                
                <div className="space-y-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Type</div>
                    <div className="font-semibold text-sm" style={{color: '#1E3A5F'}}>
                      {reportTypes.find(t => t.id === reportType)?.name}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Format</div>
                    <div className="font-semibold text-sm" style={{color: '#1E3A5F'}}>
                      {format.toUpperCase()}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Story Mode</div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {includeStory ? (
                        <>
                          <Sparkles className="w-4 h-4" style={{color: '#14B8A6'}} />
                          <span style={{color: '#14B8A6'}}>Enabled</span>
                        </>
                      ) : (
                        <span style={{color: '#6B7280'}}>Disabled</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerateReport}
                  disabled={generating}
                  className="w-full px-6 py-4 rounded-lg font-bold transition-all hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: generating ? '#9CA3AF' : 'linear-gradient(135deg, #14B8A6 0%, #10B981 100%)',
                    color: '#FFFFFF'
                  }}
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      <span>Generate Report</span>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-3">
                  Report will be generated and downloaded automatically
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-800">
                  <strong>💡 Pro Tip:</strong>
                  <p className="mt-2">
                    Enable storytelling mode for board presentations and stakeholder communications. It transforms dry data into compelling narratives that showcase real impact.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sample Reports */}
          <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold mb-4" style={{color: '#1E3A5F'}}>
              Sample Reports & Templates
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { name: 'Q3 2024 Impact Report', type: 'Quarterly', size: '2.4 MB' },
                { name: 'CSR Annual Report 2023-24', type: 'Annual', size: '5.1 MB' },
                { name: 'Skill Training Success Stories', type: 'Story', size: '1.8 MB' }
              ].map((sample, idx) => (
                <button
                  key={idx}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-teal-300 transition-all text-left"
                >
                  <div className="flex items-start gap-3">
                    <FileText className="w-8 h-8 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1 truncate" style={{color: '#1E3A5F'}}>
                        {sample.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {sample.type} • {sample.size}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportGeneration;
